import { useCallback, useMemo } from 'react';

import { TABLET } from '@app/constants';
import { useWindowSize } from '@app/hooks';

import { useExecutorSelectModel } from '@pages/Calendar/ui/ExecutorSelect/hooks';
import { useIndividualCalendarContext } from '@pages/IndividualCalendar/context';
import { useIndividualCalendarPermissions } from '@pages/IndividualCalendar/hooks';

export const useIndividualCalendarHeader = () => {
  const { innerWidth } = useWindowSize();
  const {
    currentEmployeeId,
    todayMoment,
    periodStartMoment,
    refreshCalendarData,
    returnToTodayHandler,
    openEmployeePeriodHandler,
  } = useIndividualCalendarContext();

  const { getCanAddWorkScheduleOnDate } = useIndividualCalendarPermissions({
    currentEmployeeId,
    todayMoment,
  });

  const hideActionLabels = innerWidth <= 1200 && innerWidth > TABLET;

  const canAddWorkScheduleForPeriodStart = useMemo(
    () => getCanAddWorkScheduleOnDate(periodStartMoment),
    [getCanAddWorkScheduleOnDate, periodStartMoment]
  );

  const executorSelectModel = useExecutorSelectModel({
    mode: 'individual-appointment-journal',
    currentEmployeeId,
    canCreateWorkScheduleOverride: canAddWorkScheduleForPeriodStart,
    shouldSelectExecutorOnAddWorkingDay: true,
    onSelectEmployee: openEmployeePeriodHandler,
  });

  const currentEmployeeName = useMemo(() => {
    if (executorSelectModel.currentEmployee) {
      return executorSelectModel.currentEmployee.label;
    }

    if (
      executorSelectModel.selectedExecutor?.employeeId === currentEmployeeId
    ) {
      return executorSelectModel.selectedExecutor.label;
    }

    if (executorSelectModel.isLoading) {
      return 'Загрузка исполнителя...';
    }

    return 'Исполнитель не найден';
  }, [
    currentEmployeeId,
    executorSelectModel.currentEmployee,
    executorSelectModel.isLoading,
    executorSelectModel.selectedExecutor,
  ]);

  const handleOpenCurrentEmployeeWorkSchedule = useCallback(() => {
    executorSelectModel.handleOpenAddWorkSchedule(
      currentEmployeeId,
      currentEmployeeName
    );
  }, [currentEmployeeId, currentEmployeeName, executorSelectModel]);

  return {
    hideActionLabels,
    periodStartMoment,
    refreshCalendarData,
    returnToTodayHandler,
    executorSelectModel,
    currentEmployeeName,
    hasUserRightToAddWorkSchedule: canAddWorkScheduleForPeriodStart,
    handleOpenCurrentEmployeeWorkSchedule,
  };
};
